import { makeStyles } from '@material-ui/core/styles';
import { TextColors, SecondaryColors, TextOpacities, PrimaryColors } from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

const darkTheme = {
  root: {
    '& .swiper-pagination-bullet': {
      '&:before': {
        backgroundColor: hexToRGBA(TextColors.light, TextOpacities.light2),
      },

      '&:hover:not(.swiper-pagination-bullet-active):before': {
        backgroundColor: TextColors.light,
      },

      '&.swiper-pagination-bullet-active:before': {
        backgroundColor: SecondaryColors.main,
      },
    },
    '& .dot-arrow': {
      color: hexToRGBA(TextColors.light, TextOpacities.light2),

      '&:hover': {
        color: TextColors.light,
      },
    },
  },
};

const lightTheme = {
  root: {
    '& .swiper-pagination-bullet': {
      '&:before': {
        backgroundColor: hexToRGBA(TextColors.dark, TextOpacities.dark2),
      },

      '&:hover:not(.swiper-pagination-bullet-active):before': {
        backgroundColor: TextColors.dark,
      },

      '&.swiper-pagination-bullet-active:before': {
        backgroundColor: SecondaryColors.dark,
      },
    },
    '& .dot-arrow': {
      color: hexToRGBA(TextColors.dark, TextOpacities.dark2),

      '&:hover': {
        color: TextColors.dark,
      },
    },
  },
};

const swiperShadowStyles = {
  '&.swiper-shadow': {
    '&.swiper-shadow--left:before, &.swiper-shadow--right:after': {
      content: "''",
      position: 'absolute',
      zIndex: 2,
      bottom: 0,
      top: 0,
      width: '50px',
      pointerEvents: 'none',
    },
    '&.swiper-shadow--left:before': {
      left: 0,
    },
    '&.swiper-shadow--right:after': {
      right: 0,
    },
    '&.swiper-shadow--theme-light': {
      '&.swiper-shadow--left:before': {
        boxShadow: `inset 110px 0px 25px -90px ${PrimaryColors.light}`,
      },
      '&.swiper-shadow--right:after': {
        boxShadow: `inset -110px 0px 25px -90px ${PrimaryColors.light}`,
      },
    },
    '&.swiper-shadow--theme-dark': {
      '&.swiper-shadow--left:before': {
        boxShadow: `inset 110px 0px 25px -90px ${PrimaryColors.main}`,
      },
      '&.swiper-shadow--right:after': {
        boxShadow: `inset -110px 0px 25px -90px ${PrimaryColors.main}`,
      },
    },
  },
};

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      [muiTheme.breakpoints.down('sm')]: {
        '&.dark-theme': darkTheme.root,
        '&.light-theme': lightTheme.root,
      },

      [muiTheme.breakpoints.up('md')]: {
        '&.dark-theme-md': darkTheme.root,
        '&.light-theme-md': lightTheme.root,
      },

      '& .swiper-container': {
        'margin-left': 'auto',
        'margin-right': 'auto',
        position: 'relative',
        overflow: 'hidden',
        'list-style': 'none',
        padding: '0',
        /* Fix of Webkit flickering */
        zIndex: '1',

        ...swiperShadowStyles,
      },
      '& .swiper-container-vertical > .swiper-wrapper': {
        'flex-direction': 'column',
      },
      '& .swiper-wrapper': {
        position: 'relative',
        width: '100%',
        height: '100%',
        zIndex: '1',
        display: 'flex',
        'transition-property': 'transform',
        'box-sizing': 'content-box',
      },
      '& .swiper-slide': {
        'flex-shrink': '0',
        width: '100%',
        height: '100%',
        position: 'relative',
        'transition-property': 'transform',
      },

      '& .swiper-container-android .swiper-slide, & .swiper-wrapper': {
        transform: 'translate3d(0px, 0, 0)',
      },
      '& .swiper-container-multirow > .swiper-wrapper': {
        'flex-wrap': 'wrap',
      },
      '& .swiper-container-multirow-column > .swiper-wrapper': {
        'flex-wrap': 'wrap',
        'flex-direction': 'column',
      },
      '& .swiper-container-free-mode > .swiper-wrapper': {
        'transition-timing-function': 'ease-out',
        margin: '0 auto',
      },
      '& .swiper-container-android .swiper-slide, &.swiper-wrapper': {
        transform: 'translate3d(0px, 0, 0)',
      },

      '& .swiper-slide-invisible-blank': {
        visibility: 'hidden',
      },
      '& .swiper-container-autoheight, &.swiper-container-autoheight .swiper-slide': {
        height: 'auto',
      },
      '& .swiper-container-autoheight .swiper-wrapper': {
        'align-items': 'flex-start',
        'transition-property': 'transform, height',
      },
      '& .swiper-container-3d': {
        perspective: '1200px',
      },
      '& .swiper-container-3d .swiper-wrapper, & .swiper-container-3d .swiper-slide, & .swiper-container-3d .swiper-slide-shadow-left, & .swiper-container-3d .swiper-slide-shadow-right, & .swiper-container-3d .swiper-slide-shadow-top, & .swiper-container-3d .swiper-slide-shadow-bottom, & .swiper-container-3d .swiper-cube-shadow':
        {
          transformStyle: 'preserve-3d',
        },
      '& .swiper-container-3d .swiper-slide-shadow-left, & .swiper-container-3d .swiper-slide-shadow-right, & .swiper-container-3d .swiper-slide-shadow-top, & .swiper-container-3d .swiper-slide-shadow-bottom':
        {
          position: 'absolute',
          left: '0',
          top: '0',
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: '10',
        },
      '& .swiper-container-3d .swiper-slide-shadow-left': {
        'background-image': 'linear-gradient(to left, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0))',
      },
      '& .swiper-container-3d .swiper-slide-shadow-right': {
        'background-image': 'linear-gradient(to right, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0))',
      },
      '& .swiper-container-3d .swiper-slide-shadow-top': {
        'background-image': 'linear-gradient(to top, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0))',
      },
      '& .swiper-container-3d .swiper-slide-shadow-bottom': {
        'background-image': 'linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0))',
      },
      /* CSS Mode */
      '& .swiper-container-css-mode > .swiper-wrapper': {
        overflow: 'auto',
        'scrollbar-width': 'none',
        /* For Firefox */
        '-ms-overflow-style': 'none',
        /* For Internet Explorer and Edge */
      },
      '& .swiper-container-css-mode > .swiper-wrapper::-webkit-scrollbar': {
        display: 'none',
      },
      '& .swiper-container-css-mode > .swiper-wrapper > .swiper-slide': {
        'scroll-snap-align': 'start start',
      },
      '& .swiper-container-horizontal.swiper-container-css-mode > .swiper-wrapper': {
        'scroll-snap-type': 'x mandatory',
      },
      '& .swiper-container-vertical.swiper-container-css-mode > .swiper-wrapper': {
        'scroll-snap-type': 'y mandatory',
      },

      '& .swiper-button-prev, & .swiper-button-next': {
        position: 'absolute',
        top: '50%',
        width: 'calc(var(--swiper-navigation-size) / 44 * 27)',
        height: 'var(--swiper-navigation-size)',
        'margin-top': 'calc(-1 * var(--swiper-navigation-size) / 2)',
        zIndex: '10',
        cursor: 'pointer',
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'center',
        color: 'var(--swiper-navigation-color, var(--swiper-theme-color))',
      },
      '& .swiper-button-prev.swiper-button-disabled, & .swiper-button-next.swiper-button-disabled':
        {
          opacity: '0.35',
          cursor: 'auto',
          pointerEvents: 'none',
        },
      '& .swiper-button-prev:after, & .swiper-button-next:after': {
        'font-family': 'swiper-icons',
        'font-size': 'var(--swiper-navigation-size)',
        'text-transform': 'none !important',
        'letter-spacing': '0',
        // 'text-transform': 'none',
        'font-variant': 'initial',
        'line-height': '1',
      },
      '& .swiper-button-prev, & .swiper-container-rtl .swiper-button-next': {
        left: '10px',
        right: 'auto',
      },
      '& .swiper-button-prev:after, & .swiper-container-rtl .swiper-button-next:after': {
        content: "'prev'",
      },
      '& .swiper-button-next, & .swiper-container-rtl .swiper-button-prev': {
        right: '10px',
        left: 'auto',
      },
      '& .swiper-button-next:after, &.swiper-container-rtl .swiper-button-prev:after': {
        content: "'next'",
      },
      '& .swiper-button-prev.swiper-button-white, & .swiper-button-next.swiper-button-white': {
        '--swiper-navigation-color': '#ffffff',
      },
      '& .swiper-button-prev.swiper-button-black, & .swiper-button-next.swiper-button-black': {
        '--swiper-navigation-color': '#000000',
      },
      '& .swiper-button-lock': {
        display: 'none',
      },
      '& .swiper-pagination': {
        position: 'absolute',
        'text-align': 'center',
        transition: '300ms opacity',
        transform: 'translate3d(0, 0, 0)',
        zIndex: '10',
      },
      '& .swiper-pagination.swiper-pagination-hidden': {
        opacity: '0',
      },
      /* Common Styles */
      '& .swiper-pagination-fraction, & .swiper-pagination-custom, & .swiper-container-horizontal > .swiper-pagination-bullets':
        {
          bottom: '10px',
          left: '0',
          width: '100%',
        },
      /* Bullets */
      '& .swiper-pagination-bullets-dynamic': {
        overflow: 'hidden',
        'font-size': '0',
      },
      '& .swiper-pagination-bullets-dynamic .swiper-pagination-bullet': {
        transform: 'scale(0.33)',
        position: 'relative',
      },
      '& .swiper-pagination-bullets-dynamic .swiper-pagination-bullet-active': {
        transform: 'scale(1)',
      },
      '& .swiper-pagination-bullets-dynamic .swiper-pagination-bullet-active-main': {
        transform: 'scale(1)',
      },
      '& .swiper-pagination-bullets-dynamic .swiper-pagination-bullet-active-prev': {
        transform: 'scale(0.66)',
      },
      '& .swiper-pagination-bullets-dynamic .swiper-pagination-bullet-active-prev-prev': {
        transform: 'scale(0.33)',
      },
      '& .swiper-pagination-bullets-dynamic .swiper-pagination-bullet-active-next': {
        transform: 'scale(0.66)',
      },
      '& .swiper-pagination-bullets-dynamic .swiper-pagination-bullet-active-next-next': {
        transform: 'scale(0.33)',
      },
      '& .swiper-pagination-bullet': {
        borderRadius: '10px',
        backgroundColor: 'transparent',
        width: '20px',
        height: '20px',
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        alignContent: 'center',

        '&:before': {
          borderRadius: '3.5px',
          content: '""',
          width: '7px',
          height: '7px',
        },
      },
      '& button.swiper-pagination-bullet': {
        border: 'none',
        margin: '0',
        padding: '0',
        'box-shadow': 'none',
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        appearance: 'none',
      },
      '& .swiper-pagination-clickable .swiper-pagination-bullet': {
        cursor: 'pointer',
      },
      '& .swiper-pagination-bullet-active': {},
      '& .swiper-container-vertical > .swiper-pagination-bullets': {
        right: '10px',
        top: '50%',
        transform: 'translate3d(0px, -50%, 0)',
      },
      '& .swiper-container-vertical > .swiper-pagination-bullets .swiper-pagination-bullet': {
        margin: '6px 0',
        display: 'block',
      },
      '& .swiper-container-vertical > .swiper-pagination-bullets.swiper-pagination-bullets-dynamic':
        {
          top: '50%',
          transform: 'translateY(-50%)',
          width: '8px',
        },
      '& .swiper-container-vertical > .swiper-pagination-bullets.swiper-pagination-bullets-dynamic .swiper-pagination-bullet':
        {
          display: 'inline-block',
          transition: '200ms transform, 200ms top',
        },
      '& .swiper-container-horizontal > .swiper-pagination-bullets .swiper-pagination-bullet': {
        margin: '0 4px',
      },
      '& .swiper-container-horizontal > .swiper-pagination-bullets.swiper-pagination-bullets-dynamic':
        {
          left: '50%',
          transform: 'translateX(-50%)',
          'white-space': 'nowrap',
        },
      '& .swiper-container-horizontal > .swiper-pagination-bullets.swiper-pagination-bullets-dynamic .swiper-pagination-bullet':
        {
          transition: '200ms transform, 200ms left',
        },
      '& .swiper-container-horizontal.swiper-container-rtl > .swiper-pagination-bullets-dynamic .swiper-pagination-bullet':
        {
          transition: '200ms transform, 200ms right',
        },
      /* Progress */
      '& .swiper-pagination-progressbar': {
        background: 'rgba(0, 0, 0, 0.25)',
        position: 'absolute',
      },
      '& .swiper-pagination-progressbar .swiper-pagination-progressbar-fill': {
        background: 'var(--swiper-pagination-color, var(--swiper-theme-color))',
        position: 'absolute',
        left: '0',
        top: '0',
        width: '100%',
        height: '100%',
        transform: 'scale(0)',
        'transform-origin': 'left top',
      },
      '& .swiper-container-rtl .swiper-pagination-progressbar .swiper-pagination-progressbar-fill':
        {
          'transform-origin': 'right top',
        },
      '& .swiper-container-horizontal > .swiper-pagination-progressbar, & .swiper-container-vertical > .swiper-pagination-progressbar.swiper-pagination-progressbar-opposite':
        {
          width: '100%',
          height: '4px',
          left: '0',
          top: '0',
        },
      '& .swiper-container-vertical > .swiper-pagination-progressbar, & .swiper-container-horizontal > .swiper-pagination-progressbar.swiper-pagination-progressbar-opposite':
        {
          width: '4px',
          height: '100%',
          left: '0',
          top: '0',
        },
      '& .swiper-pagination-white': {
        '--swiper-pagination-color': '#ffffff',
      },
      '& .swiper-pagination-black': {
        '--swiper-pagination-color': '#000000',
      },
      '& .swiper-pagination-lock': {
        display: 'none',
      },
      /* Scrollbar */
      '& .swiper-scrollbar': {
        'border-radius': '10px',
        position: 'relative',
        '-ms-touch-action': 'none',
        background: 'rgba(0, 0, 0, 0.1)',
      },
      '& .swiper-container-horizontal > .swiper-scrollbar': {
        position: 'absolute',
        left: '1%',
        bottom: '3px',
        zIndex: '50',
        height: '5px',
        width: '98%',
      },
      '& .swiper-container-vertical > .swiper-scrollbar': {
        position: 'absolute',
        right: '3px',
        top: '1%',
        zIndex: '50',
        width: '5px',
        height: '98%',
      },
      '& .swiper-scrollbar-drag': {
        height: '100%',
        width: '100%',
        position: 'relative',
        background: 'rgba(0, 0, 0, 0.5)',
        'border-radius': '10px',
        left: '0',
        top: '0',
      },
      '& .swiper-scrollbar-cursor-drag': {
        cursor: 'move',
      },
      '& .swiper-scrollbar-lock': {
        display: 'none',
      },
      '& .swiper-zoom-container': {
        width: '100%',
        height: '100%',
        display: 'flex',
        'justify-content': 'center',
        'align-items': 'center',
        'text-align': 'center',
      },
      '& .swiper-zoom-container > img, & .swiper-zoom-container > svg, & .swiper-zoom-container > canvas':
        {
          'max-width': '100%',
          'max-height': '100%',
          'object-fit': 'contain',
        },
      '& .swiper-slide-zoomed': {
        cursor: 'move',
      },

      '& .swiper-lazy-preloader': {
        width: '42px',
        height: '42px',
        position: 'absolute',
        left: '50%',
        top: '50%',
        'margin-left': '-21px',
        'margin-top': '-21px',
        zIndex: '10',
        'transform-origin': '50%',
        // animation: '$swiperPreloader 1s infinite linear',
        'box-sizing': 'border-box',
        border: '4px solid var(--swiper-preloader-color, var(--swiper-theme-color))',
        'border-radius': '50%',
        'border-top-color': 'transparent',
      },
      '& .swiper-lazy-preloader-white': {
        '--swiper-preloader-color': '#fff',
      },
      '& .swiper-lazy-preloader-black': {
        '--swiper-preloader-color': '#000',
      },
    },
    navigationContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignContent: 'center',
    },
    navigation: {
      display: 'flex',
      flexFlow: 'row nowrap',
      alignItems: 'center',

      '&.disabled': {
        display: 'none',
      },

      [muiTheme.breakpoints.down('sm')]: {
        margin: '0 auto',
      },
    },
    spacer: {
      flexGrow: 1,
    },
    footer: {
      marginTop: muiTheme.spacing(3),
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',

      [muiTheme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'center',
        alignContent: 'center',

        '& > * + *': {
          marginTop: '24px',
        },
      },
    },
    dots: {
      display: 'flex',
      flexFlow: 'row nowrap',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
    },
    dotArrow: {
      cursor: 'pointer',
    },
    linkContainer: {
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      alignContent: 'center',
    },
  }),
  { name: 'GenSwiperCarousel' },
);

export default useStyles;
